import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../config';
import AddressCascade from '../AddressCascade';
import staffTopoFetcher from '../staffTopoFetcher';

const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('it-IT'));

const COLS = [
  { k: 'fwa_5g', l: 'FWA 5G', t: 'ok' },
  { k: 'fwa_4g', l: 'FWA 4G', t: 'ok' },
  { k: 'fibra_10g', l: 'Fibra 10G', t: 'ok' },
  { k: 'fibra_nere', l: 'Fibra nere', t: 'ok' },
  { k: 'fibra_bianche', l: 'Fibra bianche', t: 'ok' },
  { k: 'xdsl_30m', l: 'xDSL 30M', t: 'speed' },
  { k: 'xdsl_fast200', l: 'xDSL Fast 200', t: 'speed' },
  { k: 'adsl_prof', l: 'ADSL Prof.', t: 'ok' },
  { k: 'ftto', l: 'FTTO', t: 'fascia' },
  { k: 'eth_silver', l: 'Eth. Silver', t: 'fascia' },
  { k: 'eth_gold', l: 'Eth. Gold', t: 'fascia' },
  { k: 'fibra_prof_10g', l: 'Fibra Prof. 10G', t: 'ok' },
  { k: 'fibra_prof', l: 'Fibra Prof.', t: 'ok' },
];

const TABS = [
  { id: 'RIEPILOGO' },
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof', 'ftto', 'eth_silver', 'eth_gold'] },
];
// Raggruppamento del RIEPILOGO: FTTO/Ethernity in una categoria dedicata (fibra dedicata business).
const RIEP_GRUPPI = [
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof'] },
  { id: 'DEDICATA', keys: ['ftto', 'eth_silver', 'eth_gold'] },
];

const Check = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
);

function Cell({ col, val }) {
  if (!val) return <span style={{ color: 'var(--line)' }}>—</span>;
  if (col.t === 'ok') {
    if (val.esito === 'OK') return <Check />;
    if (val.esito === 'GIALLO') return <span style={{ color: 'var(--warn)' }}>~</span>;
    return <span style={{ color: 'var(--line)' }}>—</span>;
  }
  if (col.t === 'speed') {
    return (val.esito === 'OK' && (val.up || val.down))
      ? <span>{val.up || '?'}/{val.down || '?'}</span>
      : <span style={{ color: 'var(--line)' }}>—</span>;
  }
  if (col.t === 'fascia') return <span>{val.fascia || (val.esito === 'OK' ? 'OK' : '—')}</span>;
  return <span>—</span>;
}

const th = { padding: '8px 6px', fontSize: 11, color: 'var(--muted)', fontWeight: 700, borderBottom: '1px solid var(--line)', textAlign: 'center', whiteSpace: 'nowrap' };
const td = { padding: '8px 6px', fontSize: 12.5, textAlign: 'center', borderBottom: '1px solid var(--surface-2)' };

function Riga({ ind, colonne, onOpen }) {
  return (
    <tr onClick={onOpen} style={{ cursor: onOpen ? 'pointer' : 'default' }}>
      <td style={{ ...td, textAlign: 'left', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
        {(ind?.comune || '').toLowerCase()} · {(ind?.via || '').toLowerCase()} {ind?.civico}
      </td>
      {COLS.map((c) => <td key={c.k} style={td}><Cell col={c} val={colonne?.[c.k]} /></td>)}
    </tr>
  );
}

export default function TabSalesMate() {
  const [f, setF] = useState({ provincia: '', comune: '', via: '', civico: '' });
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [curId, setCurId] = useState(null);
  const [storico, setStorico] = useState([]);
  const [stats, setStats] = useState(null);
  const poll = useRef(null);
  const [bulk, setBulk] = useState(null);   // { id, totale, fatti, stato }
  const [bulkErr, setBulkErr] = useState('');
  const [lotti, setLotti] = useState([]);   // storico lotti bulk ri-scaricabili
  const bulkPoll = useRef(null);
  const [dett, setDett] = useState(null);   // { ind, colonne } da mostrare nel dialog a tab
  const [covTab, setCovTab] = useState('RIEPILOGO');
  const apri = (ind, colonne) => { setCovTab('RIEPILOGO'); setDett({ ind, colonne }); };

  const scarica = async (url, filename) => {
    try {
      const r = await axios.get(url, { responseType: 'blob' });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = href; a.download = filename; a.click();
      URL.revokeObjectURL(href);
    } catch { setBulkErr('Errore nel download'); }
  };

  const caricaBulk = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBulkErr('');
    clearInterval(bulkPoll.current);   // ferma un eventuale poll precedente (upload multiplo)
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/ssmate/copertura/bulk`, fd);
      const id = r.data.id;
      setBulk({ id, totale: r.data.totale, fatti: 0, stato: 'in_corso' });
      bulkPoll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/ssmate/copertura/bulk/${id}`);
          setBulk((b) => ({ ...(b || {}), fatti: s.data.fatti, totale: s.data.totale, stato: s.data.stato }));
          if (['completato', 'annullato', 'errore'].includes(s.data.stato)) {
            clearInterval(bulkPoll.current);
            load();
          }
        } catch { /* ritenta */ }
      }, 2000);
    } catch (err) {
      setBulkErr(err.response?.data?.detail || 'Errore caricamento file');
    }
  };

  const annullaBulk = async () => {
    if (!bulk?.id) return;
    try { await axios.post(`${API}/ssmate/copertura/bulk/${bulk.id}/cancel`); } catch { /* ignora */ }
  };

  const load = useCallback(() => {
    axios.get(`${API}/ssmate/copertura/storico`).then((r) => setStorico(r.data?.items || [])).catch(() => {});
    axios.get(`${API}/ssmate/copertura/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/ssmate/copertura/lotti`).then((r) => setLotti(r.data?.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); return () => { clearInterval(poll.current); clearInterval(bulkPoll.current); }; }, [load]);

  const verifica = async () => {
    if (!f.provincia.trim() || !f.comune.trim() || !f.via.trim() || !f.civico.trim()) return;
    setBusy(true); setRes(null); setCurId(null);
    try {
      const r = await axios.post(`${API}/ssmate/copertura`, f);
      const id = r.data.id;
      setCurId(id);
      poll.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/ssmate/copertura/${id}`);
          if (s.data.stato === 'completato' || s.data.stato === 'errore') {
            clearInterval(poll.current);
            setBusy(false);
            setRes(s.data.stato === 'errore' ? { errore: s.data.errore } : s.data.risultato);
            load();
          }
        } catch { /* ritenta */ }
      }, 2000);
    } catch (e) {
      setBusy(false);
      setRes({ errore: e.response?.data?.detail || 'Errore avvio verifica' });
    }
  };

  return (
    <div className="cop">

      {stats && (
        <div className="cop-kpis">
          <div className="cop-kpi"><div className="cop-kpi-v">{nf(stats.totale)}</div><div className="cop-kpi-l">Verifiche</div></div>
          <div className="cop-kpi"><div className="cop-kpi-v" style={{ color: '#16a34a' }}>{nf(stats.trovati)}</div><div className="cop-kpi-l">Indirizzi trovati</div></div>
          <div className="cop-kpi"><div className="cop-kpi-v" style={{ color: '#b45309' }}>{nf(stats.non_trovati)}</div><div className="cop-kpi-l">Non trovati</div></div>
        </div>
      )}

      {stats && (
        <div className="cop-card">
          <div className="cop-card-h">Indirizzi vendibili per tecnologia</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(96px,1fr))', gap: 10 }}>
            {COLS.map((c) => (
              <div key={c.k} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{nf(stats.per_colonna?.[c.k] || 0)}</div>
                <div className="cop-kpi-l">{c.l}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cop-card">
        <div className="cop-card-h">Verifica massiva (Excel)</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn" onClick={() => scarica(`${API}/ssmate/copertura/template`, 'template-copertura-ssmate.xlsx')}>
            Scarica template
          </button>
          <label className="btn btn-primary" style={{ cursor: 'pointer', margin: 0 }}>
            Carica Excel
            <input type="file" accept=".xlsx" onChange={caricaBulk} style={{ display: 'none' }} />
          </label>
          {bulk && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13 }}>
              <div style={{ width: 180, height: 8, background: 'var(--line)', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ width: `${bulk.totale ? Math.round((bulk.fatti / bulk.totale) * 100) : 0}%`, height: '100%', background: 'var(--ok)' }} />
              </div>
              <span>{bulk.fatti} / {bulk.totale}</span>
              {bulk.stato === 'in_corso' && <button className="btn" onClick={annullaBulk}>Annulla</button>}
              {['completato', 'annullato'].includes(bulk.stato) && (
                <button className="btn btn-primary" onClick={() => scarica(`${API}/ssmate/copertura/export/${bulk.id}`, 'copertura-ssmate.xlsx')}>
                  Scarica Excel
                </button>
              )}
              {bulk.stato === 'errore' && <span style={{ color: 'var(--err)' }}>Errore elaborazione</span>}
            </div>
          )}
        </div>
        {bulkErr && <div style={{ color: 'var(--err)', marginTop: 8, fontSize: 13 }}>{bulkErr}</div>}
      </div>

      {lotti.length > 0 && (
        <div className="cop-card">
          <div className="cop-card-h">Storico lotti caricati</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lotti.map((l) => {
              const nome = l.filename || `Lotto ${(l.batch_id || '').slice(0, 8)}`;
              const dl = l.filename ? `esiti-${l.filename.replace(/\.xlsx$/i, '')}.xlsx` : `copertura-ssmate-${(l.batch_id || '').slice(0, 8)}.xlsx`;
              return (
                <div key={l.batch_id} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, flex: 1, minWidth: 140 }}>{nome}</span>
                  <span style={{ color: 'var(--muted)', whiteSpace: 'nowrap' }}>{l.totale} indirizzi</span>
                  <span style={{ color: 'var(--faint)', whiteSpace: 'nowrap' }}>{l.at ? new Date(l.at).toLocaleString('it-IT') : ''}</span>
                  <button className="btn" onClick={() => scarica(`${API}/ssmate/copertura/export/${l.batch_id}`, dl)}>Scarica di nuovo</button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="cop-card">
        <div className="cop-card-h">Verifica un indirizzo</div>
        <div style={{ width: 320, maxWidth: '100%' }}>
        <AddressCascade variant="desktop" fetcher={staffTopoFetcher} stepper
          submitLabel={busy ? 'Verifica in corso…' : 'Verifica'} submitDisabled={busy}
          btnClass="btn btn-primary" onSubmit={verifica}
          onChange={(a) => setF({ provincia: a.provincia, comune: a.comune, via: a.via, civico: a.civico })} />
        </div>
      </div>

      {res?.errore && <div style={{ color: '#ef4444' }}>{res.errore}</div>}
      {res && res.trovato === false && !res.errore && <div style={{ color: '#b45309' }}>Indirizzo non trovato su SSMATE.</div>}

      {(res?.colonne || storico.length > 0) && (
        <div className="cop-tablewrap">
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left' }}>Indirizzo</th>
                {COLS.map((c) => <th key={c.k} style={th}>{c.l}</th>)}
              </tr>
            </thead>
            <tbody>
              {res?.colonne && <Riga ind={res.indirizzo} colonne={res.colonne} onOpen={() => apri(res.indirizzo, res.colonne)} />}
              {storico.filter((s) => s.colonne && s.id !== curId).map((s) => <Riga key={s.id} ind={s.indirizzo} colonne={s.colonne} onOpen={() => apri(s.indirizzo, s.colonne)} />)}
            </tbody>
          </table>
        </div>
      )}

      {dett && (
        <div onClick={() => setDett(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, padding: 18, width: 'min(480px,96vw)', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 800, textTransform: 'capitalize' }}>{(dett.ind?.comune || '').toLowerCase()} · {(dett.ind?.via || '').toLowerCase()} {dett.ind?.civico}</div>
              <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setDett(null)}>Chiudi</button>
            </div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {TABS.map((t) => (
                <button key={t.id} onClick={() => setCovTab(t.id)} style={{
                  flex: 1, padding: '8px 6px', fontSize: 12, fontWeight: 700, cursor: 'pointer', borderRadius: 8,
                  border: '1px solid ' + (covTab === t.id ? 'var(--info)' : 'var(--line)'),
                  background: covTab === t.id ? 'var(--surface-2)' : 'var(--surface-2)', color: covTab === t.id ? 'var(--info)' : 'var(--muted)',
                }}>{t.id}</button>
              ))}
            </div>
            {(() => {
              const colonne = dett.colonne || {};
              const tab = TABS.find((t) => t.id === covTab) || TABS[0];
              const isRiep = covTab === 'RIEPILOGO';
              const keys = tab.keys || COLS.filter((c) => (colonne[c.k] || {}).esito === 'OK').map((c) => c.k);
              const groups = isRiep
                ? RIEP_GRUPPI.map((t) => ({ id: t.id, keys: t.keys.filter((k) => (colonne[k] || {}).esito === 'OK') })).filter((g) => g.keys.length)
                : [{ id: tab.id, keys }];
              const totale = groups.reduce((n, g) => n + g.keys.length, 0);
              if (!totale) return <div style={{ minHeight: 220, fontSize: 13, color: 'var(--muted)' }}>{covTab === 'RIEPILOGO' ? 'Nessuna tecnologia vendibile su questo indirizzo.' : 'Nessun dato.'}</div>;
              return <div style={{ minHeight: 220 }}>{groups.map((g, gi) => (
                <div key={g.id}>
                  {isRiep && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.6px', textTransform: 'uppercase', color: 'var(--info)', background: 'var(--surface-2)', border: '1px solid #dbe4ff', borderRadius: 8, padding: '5px 9px', margin: gi ? '12px 0 6px' : '0 0 6px' }}>{g.id}</div>}
                  {g.keys.map((k) => { const c = COLS.find((x) => x.k === k); return (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 2px', borderBottom: '1px solid var(--surface-2)', fontSize: 13 }}>
                      <span style={{ color: 'var(--muted)' }}>{c.l}</span><span><Cell col={c} val={colonne[k]} /></span>
                    </div>
                  ); })}
                </div>
              ))}</div>;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
